import { useState } from 'react';
import { Box, Popover } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import MDButton from '@components/MDButton';
import { StandaloneAutoCompleteSelect } from '@components/StandaloneAutoCompleteSelect';
import { LabelledSelect } from '@components/StandaloneAutoCompleteSelect/labelled-select';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { UserResponse } from 'layouts/pages/System/users/services/responses/user.response';
import { TagResponse } from '@layouts/pages/Settings/general/tags/services/responses/tag.response';
import { getCrmColors } from '../../crm-theme';
import { ScheduleFilter } from '../../filters/crm-schedule-filter';
import { OWNER_UNASSIGNED } from '../../filters/crm-owner-filter';
import { filtersButtonSx, filtersPopoverSx } from './crm-board-filters.styles';

interface Props {
    users: UserResponse[];
    /// Id do responsável, OWNER_UNASSIGNED ou nulo para todos.
    ownerFilter: string | null;
    onOwnerFilterChange: (owner: string | null) => void;
    tags: TagResponse[];
    /// Id da tag ou nulo para todas.
    tagFilter: string | null;
    onTagFilterChange: (tagId: string | null) => void;
    scheduleFilter: ScheduleFilter | null;
    onScheduleFilterChange: (filter: ScheduleFilter | null) => void;
}

/// Lado a lado, os filtros passavam de 600px e quebravam a barra; num botão cabem em uma linha.
export const CrmBoardFilters = ({
    users, ownerFilter, onOwnerFilterChange, tags, tagFilter, onTagFilterChange, scheduleFilter,
    onScheduleFilterChange,
}: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);

    const active = [ownerFilter, tagFilter, scheduleFilter].filter((item) => item !== null).length;

    const ownerOptions = [
        { value: OWNER_UNASSIGNED, label: t('crmKanban.semResponsavel') },
        ...users.map((user) => ({ value: user.id, label: user.name })),
    ];
    const tagOptions = tags.map((tag) => ({ value: tag.id, label: tag.name }));
    const scheduleOptions: { value: ScheduleFilter; label: string }[] = [
        { value: 'late', label: t('crmKanban.agendaAtrasados') },
        { value: 'today', label: t('crmKanban.agendaHoje') },
        { value: 'week', label: t('crmKanban.agendaSemana') },
        { value: 'none', label: t('crmKanban.agendaSemAgendamento') },
    ];

    return (
        <>
            <MDButton
                variant="outlined"
                onClick={(e) => setAnchor(e.currentTarget)}
                startIcon={<FilterListIcon sx={{ color: active ? cc.accent : cc.textSecondary }} />}
                aria-haspopup="true"
                aria-expanded={Boolean(anchor)}
                sx={filtersButtonSx(cc, active > 0)}
            >
                {t('crmKanban.filtros')}
                {active ? ` (${active})` : ''}
            </MDButton>

            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: filtersPopoverSx(cc) }}
            >
                {/* Com rótulo em cima: com valor escolhido o select perde o placeholder. */}
                <Box sx={{ display: 'grid', gap: 1.5 }}>
                    <LabelledSelect cc={cc} label={t('crmKanban.filtroResponsavel')}>
                        <StandaloneAutoCompleteSelect
                            options={ownerOptions}
                            value={ownerFilter}
                            onChange={(value) => onOwnerFilterChange((value as string | null) ?? null)}
                            placeholder={t('crmKanban.todos')}
                            isClearable
                        />
                    </LabelledSelect>

                    {/* Sem tag cadastrada o filtro não teria o que oferecer. */}
                    {tags.length > 0 && (
                        <LabelledSelect cc={cc} label={t('crmKanban.filtroTag')}>
                            <StandaloneAutoCompleteSelect
                                options={tagOptions}
                                value={tagFilter}
                                onChange={(value) => onTagFilterChange((value as string | null) ?? null)}
                                placeholder={t('crmKanban.todos')}
                                isClearable
                            />
                        </LabelledSelect>
                    )}

                    <LabelledSelect cc={cc} label={t('crmKanban.filtroAgenda')}>
                        <StandaloneAutoCompleteSelect
                            options={scheduleOptions}
                            value={scheduleFilter}
                            onChange={(value) => onScheduleFilterChange(value as ScheduleFilter | null)}
                            placeholder={t('crmKanban.todos')}
                            isClearable
                        />
                    </LabelledSelect>
                </Box>
            </Popover>
        </>
    );
};
